import { FaCalendarAlt, FaClock, FaMapMarkerAlt, FaPlus, FaMagic, FaSync, FaExternalLinkAlt, FaFileAlt } from "react-icons/fa";
import { useState, useEffect } from "react";
import MeetingScheduler from "./MeetingScheduler.jsx";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export default function Meetings() {
  const [showScheduler, setShowScheduler] = useState(false);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [pastCalendarEvents, setPastCalendarEvents] = useState([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [isLoadingPastEvents, setIsLoadingPastEvents] = useState(false);
  const [calendarError, setCalendarError] = useState(null);
  const [pastEventsError, setPastEventsError] = useState(null);

  // Fetch upcoming events from Staff Council calendar
  const fetchCalendarEvents = async () => {
    setIsLoadingEvents(true);
    setCalendarError(null);
    
    try {
      const response = await fetch(`${API_URL}/calendar/upcoming-events?maxResults=10`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch calendar events');
      }
      
      const data = await response.json();
      
      if (data.success) {
        setCalendarEvents(data.events || []);
      }
    } catch (error) {
      console.error('Error fetching calendar events:', error);
      setCalendarError(error.message);
    } finally {
      setIsLoadingEvents(false);
    }
  };

  // Fetch past events from Staff Council calendar (last 2 months)
  const fetchPastCalendarEvents = async () => {
    setIsLoadingPastEvents(true);
    setPastEventsError(null);
    
    try {
      const response = await fetch(`${API_URL}/calendar/past-events?months=2&maxResults=20`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch past calendar events');
      }
      
      const data = await response.json();
      
      if (data.success) {
        setPastCalendarEvents(data.events || []);
      }
    } catch (error) {
      console.error('Error fetching past calendar events:', error);
      setPastEventsError(error.message);
    } finally {
      setIsLoadingPastEvents(false);
    }
  };

  // Load calendar events on mount
  useEffect(() => {
    fetchCalendarEvents();
    fetchPastCalendarEvents();
  }, []);

  // Council members for scheduler - Steering Committee
  const members = [
    { id: 1, name: "Josie Simm", role: "Chair", department: "Provost Office", email: "js9640@nyu.edu" },
    { id: 2, name: "Mohammed Waseem Chaudry", role: "Vice Chair", department: "Student Affairs", email: "mwc347@nyu.edu" },
    { id: 3, name: "Karol Tolpa", role: "Academic Staff Committee Chair", department: "Social Science Division", email: "kjt9854@nyu.edu" },
    { id: 4, name: "Anand Kant Das", role: "Researcher Committee Chair", department: "Science Division", email: "akd6@nyu.edu" },
    { id: 5, name: "Eudora Okine", role: "Communications Officer", department: "At-Large Member", email: "eo50@nyu.edu" },
    { id: 6, name: "Shreekanth Nair", role: "Governance Liaison Officer", department: "Administrative and Business Operations", email: "sn1245@nyu.edu" },
    { id: 7, name: "Sabah Siddiqui", role: "HR Officer", department: "Administrative and Business Operations", email: "ss5552@nyu.edu" },
    { id: 8, name: "Amnah Khan", role: "Belonging Officer", department: "Science Division", email: "ask9190@nyu.edu" },
    { id: 9, name: "Abigail Cahill", role: "Academic Librarian", department: "Academic Staff", email: "alc9738@nyu.edu" },
    { id: 10, name: "Valeriya Golovina", role: "Arts and Humanities Division", department: "Academic Staff", email: "vig211@nyu.edu" },
    { id: 11, name: "Ibrahim Chehade", role: "At-Large Member", department: "Academic Staff", email: "iyc1@nyu.edu" },
    { id: 12, name: "Samia Ahmed", role: "Center for Writing", department: "Academic Staff", email: "sa2696@nyu.edu" },
    { id: 13, name: "Wahib Mufid Salim", role: "Engineering Division", department: "Academic Staff", email: "wms5@nyu.edu" },
    { id: 14, name: "Abigail Abbot", role: "Administrative and Business Operations", department: "Administrative Staff", email: "aya228@nyu.edu" },
    { id: 15, name: "Dimitar Dimitrov", role: "Administrative and Business Operations", department: "Administrative Staff", email: "dd2609@nyu.edu" },
  ];

  const handleScheduleMeeting = (meetingData) => {
    // Add the new meeting to the list
    const newMeeting = {
      id: meetings.length + 1,
      title: meetingData.title,
      date: meetingData.date,
      time: meetingData.time,
      location: "To Be Determined",
      description: meetingData.description,
      status: "upcoming",
    };
    setMeetings([...meetings, newMeeting]);
    setShowScheduler(false);
    
    // Refresh calendar events to show the newly created event
    setTimeout(() => {
      fetchCalendarEvents();
    }, 1000); // Small delay to allow Google Calendar to sync
  };

  return (
    <div
      style={{
        padding: "40px",
        maxWidth: "1200px",
        margin: "0 auto",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "40px",
        }}
      >
        <div>
          <h1
            style={{
              fontSize: "32px",
              fontWeight: "700",
              color: "#2d3748",
              marginBottom: "8px",
            }}
          >
            Meetings
          </h1>
          <p style={{ color: "#718096", fontSize: "16px" }}>
            Manage and view all council meetings
          </p>
        </div>
        <div style={{ display: "flex", gap: "12px" }}>
          <button
            onClick={() => setShowScheduler(true)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              border: "none",
              padding: "12px 24px",
              borderRadius: "8px",
              fontSize: "15px",
              fontWeight: "500",
              cursor: "pointer",
              boxShadow: "0 4px 6px rgba(102, 126, 234, 0.3)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 6px 12px rgba(102, 126, 234, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 6px rgba(102, 126, 234, 0.3)";
            }}
          >
            <FaMagic />
            Smart Scheduler
          </button>
          <button
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              background: "white",
              color: "#667eea",
              border: "2px solid #667eea",
              padding: "12px 24px",
              borderRadius: "8px",
              fontSize: "15px",
              fontWeight: "500",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#f7faff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "white";
            }}
          >
            <FaPlus />
            New Meeting
          </button>
        </div>
      </div>

      {/* Staff Council Calendar Events */}
      <div style={{ marginBottom: "40px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "600",
              color: "#2d3748",
              margin: 0,
            }}
          >
            📅 Staff Council Calendar Events
          </h2>
          <button
            onClick={fetchCalendarEvents}
            disabled={isLoadingEvents}
            style={{
              padding: "8px 16px",
              background: isLoadingEvents ? "#cbd5e0" : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: isLoadingEvents ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FaSync style={{ animation: isLoadingEvents ? "spin 1s linear infinite" : "none" }} />
            Refresh
          </button>
        </div>

        {calendarError && (
          <div style={{
            padding: "16px",
            background: "#fff5f5",
            border: "1px solid #feb2b2",
            borderRadius: "8px",
            color: "#c53030",
            marginBottom: "16px",
          }}>
            {calendarError}
          </div>
        )}

        {isLoadingEvents ? (
          <p style={{ color: "#718096", fontStyle: "italic" }}>Loading calendar events...</p>
        ) : calendarEvents.length > 0 ? (
          calendarEvents.map((event) => (
            <div
              key={event.id}
              style={{
                background: "white",
                borderRadius: "12px",
                padding: "24px",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
                border: "1px solid #e2e8f0",
                marginBottom: "16px",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "16px" }}>
                <h3 style={{ fontSize: "20px", fontWeight: "600", color: "#2d3748", margin: 0 }}>
                  {event.title}
                </h3>
                <a
                  href={event.htmlLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: "6px 12px",
                    background: "#f7fafc",
                    color: "#4a5568",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                    fontSize: "13px",
                    fontWeight: "500",
                    textDecoration: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  Open in Google Calendar
                  <FaExternalLinkAlt size={12} />
                </a>
              </div>
              <div style={{ display: "flex", gap: "24px", marginBottom: "12px", flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#4a5568" }}>
                  <FaCalendarAlt size={16} />
                  <span>{new Date(event.start).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#4a5568" }}>
                  <FaClock size={16} />
                  <span>{new Date(event.start).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} - {new Date(event.end).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                {event.location && (
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#4a5568" }}>
                    <FaMapMarkerAlt size={16} />
                    <span>{event.location}</span>
                  </div>
                )}
              </div>
              {event.attachments && event.attachments.length > 0 && (
                <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #e2e8f0" }}>
                  <div style={{ fontSize: "13px", fontWeight: "600", color: "#2d3748", marginBottom: "8px" }}>
                    📎 Attachments:
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    {event.attachments.map((attachment, index) => (
                      <a
                        key={index}
                        href={attachment.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "8px 12px",
                          background: "#f7fafc",
                          border: "1px solid #e2e8f0",
                          borderRadius: "6px",
                          color: "#667eea",
                          textDecoration: "none",
                          fontSize: "13px",
                          fontWeight: "500",
                          transition: "all 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#edf2f7";
                          e.currentTarget.style.borderColor = "#667eea";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#f7fafc";
                          e.currentTarget.style.borderColor = "#e2e8f0";
                        }}
                      >
                        <FaFileAlt size={14} />
                        {attachment.title || attachment.fileId}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {event.attendees && event.attendees.length > 0 && (
                <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #e2e8f0" }}>
                  <span style={{ fontSize: "13px", color: "#718096", fontWeight: "600" }}>
                    Attendees: {event.attendees.length}
                  </span>
                </div>
              )}
            </div>
          ))
        ) : (
          <p style={{ color: "#718096", fontStyle: "italic" }}>
            No upcoming events in the Staff Council calendar.
          </p>
        )}

        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>

      {/* Past Events (Last 2 Months) */}
      <div>
        <h2
          style={{
            fontSize: "24px",
            fontWeight: "600",
            color: "#2d3748",
            marginBottom: "20px",
          }}
        >
          Past Events (Last 2 Months)
        </h2>
        {isLoadingPastEvents ? (
          <p style={{ color: "#718096", fontStyle: "italic" }}>Loading past events...</p>
        ) : pastCalendarEvents.length > 0 ? (
          pastCalendarEvents.map((event) => (
            <div
              key={event.id}
              style={{
                background: "white",
                borderRadius: "12px",
                padding: "24px",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
                border: "1px solid #e2e8f0",
                marginBottom: "16px",
                opacity: 0.9,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "16px" }}>
                <h3 style={{ fontSize: "20px", fontWeight: "600", color: "#2d3748", margin: 0 }}>
                  {event.title}
                </h3>
                <a
                  href={event.htmlLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: "6px 12px",
                    background: "#f7fafc",
                    color: "#4a5568",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                    fontSize: "13px",
                    fontWeight: "500",
                    textDecoration: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  Open in Google Calendar
                  <FaExternalLinkAlt size={12} />
                </a>
              </div>
              <div style={{ display: "flex", gap: "24px", marginBottom: "12px", flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#4a5568" }}>
                  <FaCalendarAlt size={16} />
                  <span>{new Date(event.start).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#4a5568" }}>
                  <FaClock size={16} />
                  <span>{new Date(event.start).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} - {new Date(event.end).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                {event.location && (
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#4a5568" }}>
                    <FaMapMarkerAlt size={16} />
                    <span>{event.location}</span>
                  </div>
                )}
              </div>
              {event.attachments && event.attachments.length > 0 && (
                <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #e2e8f0" }}>
                  <div style={{ fontSize: "13px", fontWeight: "600", color: "#2d3748", marginBottom: "8px" }}>
                    📎 Attachments:
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    {event.attachments.map((attachment, index) => (
                      <a
                        key={index}
                        href={attachment.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "8px 12px",
                          background: "#f7fafc",
                          border: "1px solid #e2e8f0",
                          borderRadius: "6px",
                          color: "#667eea",
                          textDecoration: "none",
                          fontSize: "13px",
                          fontWeight: "500",
                          transition: "all 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#edf2f7";
                          e.currentTarget.style.borderColor = "#667eea";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#f7fafc";
                          e.currentTarget.style.borderColor = "#e2e8f0";
                        }}
                      >
                        <FaFileAlt size={14} />
                        {attachment.title || attachment.fileId}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {event.attendees && event.attendees.length > 0 && (
                <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #e2e8f0" }}>
                  <span style={{ fontSize: "13px", color: "#718096", fontWeight: "600" }}>
                    Attendees: {event.attendees.length}
                  </span>
                </div>
              )}
            </div>
          ))
        ) : (
          <p style={{ color: "#718096", fontStyle: "italic" }}>
            {pastEventsError ? `Error: ${pastEventsError}` : 'No past events from Google Calendar in the last 2 months.'}
          </p>
        )}
      </div>

      {/* Smart Meeting Scheduler Modal */}
      {showScheduler && (
        <MeetingScheduler
          members={members}
          onClose={() => setShowScheduler(false)}
          onSchedule={handleScheduleMeeting}
        />
      )}
    </div>
  );
}

